'use client'
import { queryParamsSerializer } from '@core/use-query-params/use-query-params.types'
import { useRouter, useSearchParams } from 'next/navigation'
import { createSerializer } from 'nuqs'
import { useCallback, useRef } from 'react'

import { ROUTES } from '../routes'
import {
  IQuery,
  IUsePickingRedirectReturn,
} from './use-redirect-with-query.types'

export function useRedirectWithQuery() {
  const router = useRouter()
  const searchParams = useSearchParams()

  const serializerRef = useRef(createSerializer(queryParamsSerializer))

  const redirectWithQuery = useCallback(
    function (route: string, query?: IQuery) {
      if (typeof query === 'string') {
        router.push(`${route}?${query}`)
      } else {
        const mountedRoute = serializerRef.current(
          `${route}?${searchParams?.toString()}`,
          {
            ...query,
          }
        )
        router.push(mountedRoute)
      }
    },
    [router, searchParams]
  )

  return { redirectWithQuery }
}

function useCustomRedirect(pathCompose?: (path: string) => string) {
  const { redirectWithQuery } = useRedirectWithQuery()

  const defaultPathCompose = useRef((path: string) => path)

  const redirectToPicking = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(
        (pathCompose ?? defaultPathCompose.current)(ROUTES.picking()),
        query
      ),
    [pathCompose, redirectWithQuery]
  )
  const redirectToResume = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(
        (pathCompose ?? defaultPathCompose.current)(ROUTES.resume()),
        query
      ),
    [pathCompose, redirectWithQuery]
  )
  const redirectToPickingList = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(
        (pathCompose ?? defaultPathCompose.current)(ROUTES.pickingList()),
        query
      ),
    [pathCompose, redirectWithQuery]
  )
  const redirectToEdit = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(
        (pathCompose ?? defaultPathCompose.current)(ROUTES.edit()),
        query
      ),
    [pathCompose, redirectWithQuery]
  )

  return {
    redirectToPicking,
    redirectToResume,
    redirectToPickingList,
    redirectToEdit,
  }
}

export function useToRedirect(): IUsePickingRedirectReturn {
  const pathWithTo = useCallback(
    (pathname: string) => `${ROUTES.to()}${pathname}`,
    []
  )
  const { redirectWithQuery } = useRedirectWithQuery()
  const redirects = useCustomRedirect(pathWithTo)

  const redirectToViewResume = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(pathWithTo(ROUTES.viewResume()), query),
    [pathWithTo, redirectWithQuery]
  )

  const redirectToMovementPickingList = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(pathWithTo(ROUTES.movementPickingList()), query),
    [pathWithTo, redirectWithQuery]
  )

  return {
    redirectToViewResume,
    redirectToMovementPickingList,
    ...redirects,
  }
}

export function useDeliveryRedirect(): IUsePickingRedirectReturn {
  const { redirectWithQuery } = useRedirectWithQuery()
  const redirects = useCustomRedirect()

  const redirectToPacking = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.packing(), query),
    [redirectWithQuery]
  )

  return {
    redirectToPacking,
    ...redirects,
  }
}

export function useDeliveryMovementRedirect(): IUsePickingRedirectReturn {
  const pathWithDeliveryMovement = useCallback(
    (pathname: string) => `${ROUTES.deliveryMovement()}${pathname}`,
    []
  )
  const { redirectWithQuery } = useRedirectWithQuery()
  const redirects = useCustomRedirect(pathWithDeliveryMovement)

  const redirectToPacking = useCallback(
    (query?: IQuery) =>
      redirectWithQuery(pathWithDeliveryMovement(ROUTES.packing()), query),
    [pathWithDeliveryMovement, redirectWithQuery]
  )

  return {
    redirectToPacking,
    ...redirects,
  }
}

export function useCheckingRedirect() {
  const { redirectWithQuery } = useRedirectWithQuery()

  const redirectToCheckingList = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.checking(), query),
    [redirectWithQuery]
  )

  const redirectToCheckingProduct = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.checkingProduct(), query),
    [redirectWithQuery]
  )

  const redirectToCheckingResume = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.checkingResume(), query),
    [redirectWithQuery]
  )

  const redirectToCheckingViewResume = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.checkingViewResume(), query),
    [redirectWithQuery]
  )

  return {
    redirectToCheckingList,
    redirectToCheckingProduct,
    redirectToCheckingResume,
    redirectToCheckingViewResume,
  }
}

export function useReceiveTaskRedirect() {
  const { redirectWithQuery } = useRedirectWithQuery()

  const redirectToReceiveTaskList = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.receiveTasks(), query),
    [redirectWithQuery]
  )

  const redirectToReceiveTaskCreate = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.receiveTaskCreate(), query),
    [redirectWithQuery]
  )

  const redirectToReceiveTaskEdit = useCallback(
    (query?: IQuery) => redirectWithQuery(ROUTES.receiveTaskEdit(), query),
    [redirectWithQuery]
  )

  return {
    redirectToReceiveTaskList,
    redirectToReceiveTaskCreate,
    redirectToReceiveTaskEdit,
  }
}
